<script setup>
const { isEdit = false, initialForm = {} } = defineProps({
  isEdit: Boolean,
  initialForm: Object,
});

const emits = defineEmits(["onSave"]);

const academicForm = ref({
  qualification: null,
  certificate: null,
  institution_name: null,
  university_name: null,
  programName: null,
  fieldOfStudy: null,
  student_type: null,
  startDate: null,
  endDate: null,
});

const visible = ref(false);
const activeTab = ref("0");

// 1: High School
// 2: Diploma
// 3: Degrees
const selectedQualification = ref("");

// searches
const universitySearch = ref("");
const degreeSearch = ref("");
const fieldOfStudySearch = ref("");

const institutionSearch = ref("");
const diplomaSearch = ref("");

// newly added values
const newlyAddedUniversity = ref([]);
const newlyAddedDegree = ref([]);
const newlyAddedInstitution = ref([]);
const newlyAddedDiploma = ref([]);
const otherCertificate = ref("");

// same server api fetches
const { data: studyAreas } = useFetch("/api/study-area");
const { data: certificates } = useFetch("/api/certificates");
const { data: eduLevels } = useFetch("/api/education-levels");
const { data: institutions } = useFetch(
  () => `/api/institutions?name=${institutionSearch.value}`,
  {
    watch: [institutionSearch],
    lazy: true,
  },
);
const { data: diplomas } = useFetch(
  () => `/api/diplomas?name=${diplomaSearch.value}`,
  {
    watch: [diplomaSearch],
    lazy: true,
  },
);

// universities and it's programs will be
// fetched from the backend api
const { data: universities } = usePublicFetch(
  () => `universities?name=${universitySearch.value}`,
  {
    key: "all-universities",
    watch: [universitySearch],
    lazy: true,
    transform(response) {
      return response?.data || [];
    },
  },
);
const { data: programs } = useApiFetch(
  () => `programs?name=${degreeSearch.value}`,
  {
    key: "explore-programs",
    watch: [degreeSearch],
    lazy: true,
    transform(response) {
      return response?.data || [];
    },
  },
);

const startYears = computed(() => {
  const numberOfYears = 15;
  const currentYear = new Date().getFullYear();
  return [
    ...Array.from({ length: numberOfYears - 1 }, (_, index) => {
      const year = currentYear - index - 1;
      return { label: year.toString(), value: year.toString() };
    }),
  ];
});

const endYears = computed(() => {
  const numberOfYears = 15;
  const currentYear = new Date().getFullYear();
  const years = [
    ...Array.from({ length: numberOfYears - 1 }, (_, index) => {
      const year = currentYear - index - 1;
      return { label: year.toString(), value: year.toString() };
    }),
  ];
  if (academicForm.value.student_type === "existing")
    years.unshift({ label: "Present", value: "Present" });

  return years;
});

// filtering and search
const filteredInstitutions = computed(() => {
  let allInstitutions = institutions?.value || [];

  if (
    academicForm.value.institution_name &&
    !allInstitutions.some(
      (inst) =>
        inst.name.toLowerCase() ===
        academicForm.value.institution_name.toLowerCase(),
    )
  ) {
    allInstitutions = [
      { id: "custom", name: academicForm.value.institution_name },
      ...allInstitutions,
    ];
  }

  return allInstitutions.filter(
    (inst) =>
      !newlyAddedInstitution.value
        .map((addedInst) => addedInst?.toLowerCase())
        .includes(inst.name.toLowerCase()),
  );
});

const filteredUniversities = computed(() => {
  let allUniversities = universities?.value || [];

  if (
    academicForm.value.university_name &&
    !allUniversities.some(
      (uni) => uni.name === academicForm.value.university_name,
    )
  ) {
    allUniversities = [
      { id: "custom", name: academicForm.value.university_name },
      ...allUniversities,
    ];
  }

  return allUniversities.filter(
    (uni) =>
      !newlyAddedUniversity.value
        .map((uni) => uni?.toLowerCase())
        .includes(uni.name.toLowerCase()),
  );
});

const filteredDegrees = computed(() => {
  let allPrograms = programs?.value || [];

  if (
    academicForm.value.programName &&
    !allPrograms.some(
      (pg) =>
        pg.name.toLowerCase() === academicForm.value.programName.toLowerCase(),
    )
  ) {
    allPrograms = [
      { id: "custom", name: academicForm.value.programName },
      ...allPrograms,
    ];
  }

  return allPrograms.filter(
    (pg) =>
      !newlyAddedDegree.value
        .map((addedPg) => addedPg?.toLowerCase())
        .includes(pg.name.toLowerCase()),
  );
});

const filteredDiplomas = computed(() => {
  let allPrograms = diplomas?.value || [];

  if (
    academicForm.value.programName &&
    !allPrograms.some(
      (pg) =>
        pg.name.toLowerCase() === academicForm.value.programName.toLowerCase(),
    )
  ) {
    allPrograms = [
      { id: "custom", name: academicForm.value.programName },
      ...allPrograms,
    ];
  }

  return allPrograms.filter(
    (pg) =>
      !newlyAddedDiploma.value
        .map((addedPg) => addedPg?.toLowerCase())
        .includes(pg.name.toLowerCase()),
  );
});

const filteredCertificate = computed(() => {
  let allCertificates = certificates?.value || [];

  const isCertificateValid = allCertificates.some(
    (cert) =>
      cert.name.toLowerCase() === academicForm.value.certificate?.toLowerCase(),
  );

  if (academicForm.value.certificate && !isCertificateValid) {
    otherCertificate.value = academicForm.value.certificate;
    academicForm.value.certificate = "Other";
  }

  return allCertificates;
});

// tabs logic
const onSelectQualification = (val) => {
  selectedQualification.value = val;
  academicForm.value.qualification = val;
  activeTab.value = "1";
  console.log("Academic Form: ", academicForm.value);
};

// form updates
const onSelectCertificate = (val) => {
  academicForm.value.certificate = val;
  console.log("Academic Form: ", academicForm.value);
};

const onUpdateInstitution = (val) => {
  academicForm.value.institution_name = val;
  if (!newlyAddedInstitution.value.includes(val)) {
    newlyAddedInstitution.value.push(val);
  }
  institutionSearch.value = "";
  activeTab.value = "2";
  console.log("Academic Form: ", academicForm.value);
};

const onUpdateUniversity = (val) => {
  academicForm.value.university_name = val;
  if (!newlyAddedUniversity.value.includes(val)) {
    newlyAddedUniversity.value.push(val);
  }
  universitySearch.value = "";
  activeTab.value = "2";
  console.log("Academic Form: ", academicForm.value);
};

const onUpdateDiploma = (val) => {
  academicForm.value.programName = val;
  if (!newlyAddedDiploma.value.includes(val)) {
    newlyAddedDiploma.value.push(val);
  }
  diplomaSearch.value = "";
  activeTab.value = "3";
  console.log("Academic Form: ", academicForm.value);
};

const onUpdateDegree = (val) => {
  academicForm.value.programName = val;
  if (!newlyAddedDegree.value.includes(val)) {
    newlyAddedDegree.value.push(val);
  }
  degreeSearch.value = "";
  activeTab.value = "3";
  console.log("Academic Form: ", academicForm.value);
};

const onUpdateFieldOfStudy = (val) => {
  academicForm.value.fieldOfStudy = val;
  fieldOfStudySearch.value = "";
  if (academicForm.value.qualification !== "Diploma") {
    activeTab.value = "4";
  }
  console.log("Academic Form: ", academicForm.value);
};

const hasEmptyValues = (obj) => {
  const allowedNulls = {
    "High School": [
      "institution_name",
      "university_name",
      "programName",
      "fieldOfStudy",
      "student_type",
      "startDate",
      "endDate",
    ],
    Diploma: [
      "certificate",
      "university_name",
      "student_type",
      "startDate",
      "endDate",
    ],
    "Bachelor Degree": ["certificate", "institution_name", "endDate"],
    "Master Degree": ["certificate", "institution_name", "endDate"],
    "Ph.D": ["certificate", "institution_name", "endDate"],
  };

  const allowedKeys = allowedNulls[selectedQualification.value] || [];

  return Object.entries(obj).some(([key, value]) => {
    return (value === "" || value == null) && !allowedKeys.includes(key);
  });
};

const onSubmit = async () => {
  const qualification = selectedQualification.value;
  let formData = {
    ...academicForm.value,
  };

  if (otherCertificate.value && academicForm.value.certificate === "Other") {
    formData.certificate = otherCertificate.value;
  }

  const disallowedKeys = {
    "High School": [
      "institution_name",
      "university_name",
      "programName",
      "fieldOfStudy",
      "student_type",
      "startDate",
      "endDate",
    ],
    Diploma: [
      "certificate",
      "university_name",
      "student_type",
      "startDate",
      "endDate",
    ],
    "Bachelor Degree": ["certificate", "institution_name"],
    "Master Degree": ["certificate", "institution_name"],
    "Ph.D": ["certificate", "institution_name"],
  };

  const keysToNullify = disallowedKeys[qualification] || [];

  Object.keys(formData).forEach((key) => {
    if (keysToNullify.includes(key)) {
      formData[key] = null;
    }
  });

  console.log("Submitted Form Data: ", formData);
  emits("onSave", formData);
  visible.value = false;
};

watch(
  () => academicForm.value.student_type,
  async (newFormData, oldFormData) => {
    if (newFormData === "existing") {
      academicForm.value.endDate = "Present";
    } else if (
      newFormData !== "existing" &&
      academicForm.value.endDate == "Present"
    ) {
      academicForm.value.endDate = "";
    }
  },
);

// populating the initial form data
onMounted(() => {
  if (initialForm) {
    academicForm.value = { ...initialForm };
    selectedQualification.value = initialForm.qualification;
    console.log("Inital Form Data set");
    console.log(academicForm.value);
  }
});
</script>

<template>
  <Button
    v-if="isEdit"
    severity="secondary"
    icon="pi pi-pencil"
    size="small"
    text
    @click="visible = true"
  />
  <Button
    v-else
    label="Add Highest Qualification"
    icon="pi pi-plus"
    size="small"
    outlined
    rounded
    @click="visible = true"
  />

  <Dialog
    v-model:visible="visible"
    modal
    :showHeader="false"
    :style="{ width: '35rem' }"
    :pt="{
      content: 'p-4',
    }"
  >
    <Tabs v-model:value="activeTab">
      <TabList class="overflow-x-scroll max-w-[350px] lg:max-w-full">
        <Tab value="0">Qualification</Tab>

        <!-- First tab -->
        <Tab value="1" v-if="selectedQualification === 'High School'">
          Certificate
        </Tab>
        <Tab value="1" v-else-if="selectedQualification === 'Diploma'">
          Institution
        </Tab>
        <Tab
          value="1"
          v-else-if="
            ['Bachelor Degree', 'Master Degree', 'Ph.D'].includes(
              selectedQualification,
            )
          "
        >
          University
        </Tab>

        <!-- Second tab -->
        <Tab
          value="2"
          v-if="
            academicForm.institution_name &&
            academicForm.qualification === 'Diploma'
          "
        >
          Diploma
        </Tab>
        <Tab
          value="2"
          v-if="
            academicForm.university_name &&
            ['Bachelor Degree', 'Master Degree', 'Ph.D'].includes(
              selectedQualification,
            )
          "
          >Degree</Tab
        >

        <!-- Third tab -->
        <Tab value="3" v-if="academicForm.programName">Field of study</Tab>
        <Tab
          value="4"
          v-if="
            academicForm.fieldOfStudy &&
            academicForm.qualification !== 'Diploma'
          "
          >Study year</Tab
        >
      </TabList>

      <TabPanels>
        <!-- Select education levels -->
        <TabPanel value="0">
          <div class="h-80 overflow-auto my-2">
            <div
              v-for="level in eduLevels"
              :key="level.id"
              class="text-sm cursor-pointer px-2.5 py-2 flex items-center gap-x-3"
            >
              <RadioButton
                v-model="academicForm.qualification"
                :inputId="`qualification-${level.id}`"
                name="qualificationSelection"
                :value="level.name"
                @change="onSelectQualification(level.name)"
              />
              <label :for="`qualification-${level.id}`">{{ level.name }}</label>
            </div>
          </div>
        </TabPanel>

        <!-- First panel -->
        <TabPanel
          value="1"
          v-if="selectedQualification === 'High School'"
          class="my-2"
        >
          <div
            v-for="certificate in filteredCertificate"
            :key="certificate.id"
            class="text-sm cursor-pointer px-2.5 py-2 flex items-center gap-x-3"
          >
            <RadioButton
              v-model="academicForm.certificate"
              :inputId="`cert-${certificate.id}`"
              name="certificateSelection"
              :value="certificate.name"
              @change="onSelectCertificate(certificate.name)"
            />
            <label :for="`cert-${certificate.id}`">{{
              certificate.name
            }}</label>
          </div>
          <div v-if="academicForm.certificate === 'Other'" class="my-5">
            <label
              for="customCertificateInput"
              class="block text-sm font-medium mb-3"
            >
              Please specify
            </label>
            <InputText
              id="customCertificateInput"
              v-model="otherCertificate"
              placeholder="Please provide your certificate name here…"
              class="w-full"
            />
          </div>
        </TabPanel>
        <TabPanel value="1" v-if="selectedQualification === 'Diploma'">
          <div class="mt-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="institutionSearch"
                placeholder="Create or Search Institution"
                class="w-full"
              />
            </IconField>
          </div>
          <div class="h-72 overflow-auto my-2">
            <p
              v-for="name in newlyAddedInstitution.filter(
                (newInstitute) => !!newInstitute,
              )"
              :key="name"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.institution_name === name ? 'bg-tertiary-300' : '',
              ]"
              @click="onUpdateInstitution(name)"
            >
              {{ name }}
            </p>
            <p
              v-for="institution in filteredInstitutions"
              :key="institution.id"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.institution_name === institution.name
                  ? 'bg-tertiary-300'
                  : '',
              ]"
              @click="onUpdateInstitution(institution.name)"
            >
              {{ institution.name }}
            </p>
            <p
              v-if="institutionSearch && institutionSearch.length > 0"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              @click="onUpdateInstitution(institutionSearch)"
            >
              {{ institutionSearch }}
            </p>
          </div>
        </TabPanel>
        <TabPanel
          value="1"
          v-if="
            ['Bachelor Degree', 'Master Degree', 'Ph.D'].includes(
              selectedQualification,
            )
          "
        >
          <div class="mt-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="universitySearch"
                placeholder="Create or Search University"
                class="w-full"
              />
            </IconField>
          </div>
          <div class="h-72 overflow-auto my-2">
            <p
              v-for="name in newlyAddedUniversity.filter(
                (newUniversity) => !!newUniversity,
              )"
              :key="name"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.university_name === name ? 'bg-tertiary-300' : '',
              ]"
              @click="onUpdateUniversity(name)"
            >
              {{ name }}
            </p>
            <p
              v-for="university in filteredUniversities"
              :key="university.id"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.university_name === university.name
                  ? 'bg-tertiary-300'
                  : '',
              ]"
              @click="onUpdateUniversity(university.name)"
            >
              {{ university.name }}
            </p>
            <p
              v-if="universitySearch && universitySearch.length > 0"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              @click="onUpdateUniversity(universitySearch)"
            >
              {{ universitySearch }}
            </p>
          </div>
        </TabPanel>

        <!-- second tab -->
        <TabPanel
          value="2"
          v-if="
            academicForm.institution_name &&
            academicForm.qualification === 'Diploma'
          "
        >
          <div class="mt-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="diplomaSearch"
                placeholder="Create or Search Diploma"
                class="w-full"
              />
            </IconField>
          </div>
          <div class="h-72 overflow-auto my-2">
            <p
              v-for="name in newlyAddedDiploma.filter(
                (newDiploma) => !!newDiploma,
              )"
              :key="name"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.programName === name ? 'bg-tertiary-300' : '',
              ]"
              @click="onUpdateDiploma(name)"
            >
              {{ name }}
            </p>
            <p
              v-for="diploma in filteredDiplomas"
              :key="diploma.id"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.programName === diploma.name
                  ? 'bg-tertiary-300'
                  : '',
              ]"
              @click="onUpdateDiploma(diploma.name)"
            >
              {{ diploma.name }}
            </p>
            <p
              v-if="diplomaSearch && diplomaSearch.length > 0"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              @click="onUpdateDiploma(diplomaSearch)"
            >
              {{ diplomaSearch }}
            </p>
          </div>
        </TabPanel>
        <TabPanel
          value="2"
          v-if="
            academicForm.university_name &&
            ['Bachelor Degree', 'Master Degree', 'Ph.D'].includes(
              academicForm.qualification,
            )
          "
        >
          <div class="mt-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="degreeSearch"
                placeholder="Create or Search Degree"
                class="w-full"
              />
            </IconField>
          </div>
          <div class="h-72 overflow-auto my-2">
            <p
              v-for="name in newlyAddedDegree.filter(
                (newDegree) => !!newDegree,
              )"
              :key="name"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.programName === name ? 'bg-tertiary-300' : '',
              ]"
              @click="onUpdateDegree(name)"
            >
              {{ name }}
            </p>
            <p
              v-for="degree in filteredDegrees"
              :key="degree.id"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.programName === degree.name
                  ? 'bg-tertiary-300'
                  : '',
              ]"
              @click="onUpdateDegree(degree.name)"
            >
              {{ degree.name }}
            </p>
            <p
              v-if="degreeSearch && degreeSearch.length > 0"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              @click="onUpdateDegree(degreeSearch)"
            >
              {{ degreeSearch }}
            </p>
          </div>
        </TabPanel>

        <!-- third tab -->
        <TabPanel value="3" v-if="selectedQualification !== 'High School'">
          <div class="mt-2">
            <IconField>
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="fieldOfStudySearch"
                placeholder="Search field of study"
                class="w-full"
              />
            </IconField>
          </div>
          <div class="h-72 overflow-auto my-2">
            <p
              v-for="study in studyAreas.filter(
                (studyArea) =>
                  fieldOfStudySearch?.length == 0 ||
                  studyArea.value
                    .toLowerCase()
                    .includes(fieldOfStudySearch.toLowerCase()),
              )"
              :key="study.value"
              class="text-sm cursor-pointer px-2.5 py-2 rounded-md hover:bg-slate-200"
              :class="[
                academicForm.fieldOfStudy === study.value
                  ? 'bg-tertiary-300'
                  : '',
              ]"
              @click="onUpdateFieldOfStudy(study.value)"
            >
              {{ study.value }}
            </p>
          </div>
        </TabPanel>

        <!-- fourth tab -->
        <TabPanel value="4">
          <div class="mb-2 space-y-4 h-72">
            <div class="space-y-3 my-2">
              <Label isRequired> Tell us who you are </Label>

              <div class="flex gap-6">
                <div class="flex items-center">
                  <RadioButton
                    required
                    v-model="academicForm.student_type"
                    inputId="alumni"
                    name="alumni"
                    value="alumni"
                  />
                  <label
                    for="alumni"
                    class="ml-2 text-[#4B5563] text-sm cursor-pointer"
                    >Alumni</label
                  >
                </div>
                <div class="flex items-center">
                  <RadioButton
                    required
                    v-model="academicForm.student_type"
                    inputId="existing"
                    name="existing"
                    value="existing"
                  />
                  <label
                    for="existing"
                    class="ml-2 text-[#4B5563] text-sm cursor-pointer"
                    >Existing Student</label
                  >
                </div>
              </div>
            </div>
            <div class="space-y-1">
              <Label isRequired>Study year</Label>
              <div class="flex justify-between gap-4">
                <Select
                  :options="startYears"
                  optionLabel="label"
                  optionValue="value"
                  v-model="academicForm.startDate"
                  placeholder="Start Year"
                />

                <Select
                  :options="endYears"
                  optionLabel="label"
                  optionValue="value"
                  v-model="academicForm.endDate"
                  placeholder="End Year"
                  :disabled="academicForm.student_type === 'existing'"
                />
              </div>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <hr />

    <div class="flex justify-end gap-2 pt-2">
      <Button outlined label="Cancel" @click="visible = false" />
      <Button
        label="Submit"
        :disabled="hasEmptyValues(academicForm)"
        @click="onSubmit"
      ></Button>
    </div>
  </Dialog>
</template>
